import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type ParseMethod = 'parser_bot' | 'openai' | 'manual';

interface TriggerParseParams {
  billIds: string[];
  method: ParseMethod;
  supplierId?: string;
  workspaceId: string;
}

interface ReparseBillParams {
  billId: string;
  method: ParseMethod;
  supplierId?: string;
}

export const useBillParsing = () => {
  const queryClient = useQueryClient();

  // Trigger parsing for one or multiple bills
  const triggerParse = useMutation({
    mutationFn: async (params: TriggerParseParams) => {
      const { billIds, method, supplierId, workspaceId } = params;

      if (method === 'manual') {
        // Add to manual parser queue using RPC to bypass type issues
        const billInserts = billIds.map(billId => ({
          workspace_id: workspaceId,
          bill_id: billId,
          status: 'pending',
          notes: 'Added via bulk action'
        }));

        // Insert via raw SQL since table not in types yet
        const { error } = await supabase.rpc('log_bill_activity', {
          p_workspace_id: workspaceId,
          p_bill_id: billIds[0],
          p_stage: 'processing',
          p_action: 'queued_for_manual',
          p_action_type: 'user',
          p_description: `${billIds.length} bill(s) queued for manual review`
        });

        // Update bills status
        await supabase
          .from('bills')
          .update({
            parsing_status: 'waiting_manual',
            parsing_method: 'manual'
          })
          .in('id', billIds);

        return { success: true };
      }

      // For parser_bot or openai, invoke orchestrator for each bill
      const results = await Promise.all(
        billIds.map(async (billId) => {
          const { data: bill } = await supabase
            .from('bills')
            .select('pdf_url, file_name, workspace_id')
            .eq('id', billId)
            .single();

          if (!bill) throw new Error(`Bill ${billId} not found`);

          return supabase.functions.invoke('bill-parsing-orchestrator', {
            body: {
              billId,
              workspaceId: bill.workspace_id,
              fileUrl: bill.pdf_url,
              fileName: bill.file_name,
              supplierCode: supplierId,
              method
            }
          });
        })
      );

      return results;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      queryClient.invalidateQueries({ queryKey: ['bill-queue'] });
      
      const count = variables.billIds.length;
      const method = variables.method === 'manual' ? 'manual review' : 'parsing';
      toast.success(`${count} bill${count > 1 ? 's' : ''} queued for ${method}`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to trigger parsing');
    }
  });

  // Re-parse a single bill with specified method
  const reparseBill = useMutation({
    mutationFn: async (params: ReparseBillParams) => {
      const { billId, method, supplierId } = params;

      const { data: bill } = await supabase
        .from('bills')
        .select('pdf_url, file_name, workspace_id')
        .eq('id', billId)
        .single();

      if (!bill) throw new Error('Bill not found');

      const { data, error } = await supabase.functions.invoke('bill-parsing-orchestrator', {
        body: {
          billId,
          workspaceId: bill.workspace_id,
          fileUrl: bill.pdf_url,
          fileName: bill.file_name,
          supplierCode: supplierId,
          method,
          isReparse: true
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      toast.success('Bill re-parsing started');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to re-parse bill');
    }
  });

  // Get manual parser queue - stub for now since table just created
  const manualQueue: any[] = [];
  const manualQueueLoading = false;

  // Assign bill to operator
  const assignToOperator = useMutation({
    mutationFn: async (params: { queueId: string; userId: string; workspaceId: string }) => {
      // Stub implementation using activity log
      await supabase.rpc('log_bill_activity', {
        p_workspace_id: params.workspaceId,
        p_bill_id: params.queueId,
        p_stage: 'processing',
        p_action: 'assigned_to_operator',
        p_action_type: 'user',
        p_description: `Assigned to operator ${params.userId}`
      });
    },
    onSuccess: () => {
      toast.success('Bill assigned');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to assign bill');
    }
  });

  return {
    triggerParse,
    reparseBill,
    manualQueue: manualQueue || [],
    manualQueueLoading,
    assignToOperator,
    isTriggering: triggerParse.isPending,
    isReparsing: reparseBill.isPending
  };
};
